import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useConfiguration, useUpdateLightingConfiguration } from "@/hooks";
import { DEFAULT_OPTIONS } from "@/lib/constants";
import { useForm } from "react-hook-form";
import {
  LightingConfigurationRequest,
  LightingConfigurationRequestSchema,
} from "@/api";
import { zodResolver } from "@hookform/resolvers/zod";

// Import Shadcn Form components
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";

export function LightingConfiguration() {
  // query hook
  const { data: configuration, isLoading: configurationLoading } = useConfiguration();
  // mutation hooks
  const lightingConfiguration = useUpdateLightingConfiguration();

  const lightingForm = useForm<LightingConfigurationRequest>({
    resolver: zodResolver(LightingConfigurationRequestSchema),
    values: {
      wallReflectionLimit:
        configuration?.lWallReflectionLimit ??
        Number(DEFAULT_OPTIONS.LIGHTING.WALL_REFLECTION_LIMIT),
      workPlaneReflectionLimit:
        configuration?.lWorkPlaneReflectionLimit ??
        Number(DEFAULT_OPTIONS.LIGHTING.WORK_PLANE_REFLECTION_LIMIT),
      lengthRounding:
        configuration?.lLengthRounding ??
        Number(DEFAULT_OPTIONS.LIGHTING.DISTANCE_ROUNDING),
      icRounding:
        configuration?.lIcRounding ??
        Number(DEFAULT_OPTIONS.LIGHTING.IC_ROUNDING),
      luxRounding:
        configuration?.lLuxRounding ??
        Number(DEFAULT_OPTIONS.LIGHTING.LUX_ROUNDING),
      reflectionRounding:
        configuration?.lReflectionRounding ??
        Number(DEFAULT_OPTIONS.LIGHTING.REFLECTION_ROUNDING),
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Configuración de Iluminación</CardTitle>
        <CardDescription>
          Configure los parámetros para los reportes de iluminación.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-6">
        <Form {...lightingForm}>
          <form className="grid gap-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={lightingForm.control}
                name="wallReflectionLimit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Límite de Reflexión de Pared</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        value={field.value}
                        onChange={(e) =>
                          field.onChange(Number(e.target.value))
                        }
                        step="0.1"
                        className="w-24"
                      />
                    </FormControl>
                    <FormDescription>
                      Límite de reflexión permitido para paredes.
                    </FormDescription>
                  </FormItem>
                )}
              />
              <FormField
                control={lightingForm.control}
                name="workPlaneReflectionLimit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Límite de Reflexión del Plano de Trabajo
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        value={field.value}
                        onChange={(e) =>
                          field.onChange(Number(e.target.value))
                        }
                        step="0.1"
                        className="w-24"
                      />
                    </FormControl>
                    <FormDescription>
                      Límite de reflexión permitido para el plano de
                      trabajo.
                    </FormDescription>
                  </FormItem>
                )}
              />
            </div>

            <Separator className="my-2" />
            <h3 className="text-sm font-medium">
              Configuración de Precisión
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={lightingForm.control}
                name="lengthRounding"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Redondeo de Distancia</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        value={field.value}
                        onChange={(e) =>
                          field.onChange(Number(e.target.value))
                        }
                        step="1"
                        className="w-24"
                      />
                    </FormControl>
                    <FormDescription>
                      Número de decimales para medidas de distancia.
                    </FormDescription>
                  </FormItem>
                )}
              />
              <FormField
                control={lightingForm.control}
                name="icRounding"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Redondeo de IC</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        value={field.value}
                        onChange={(e) =>
                          field.onChange(Number(e.target.value))
                        }
                        step="1"
                        className="w-24"
                      />
                    </FormControl>
                    <FormDescription>
                      Número de decimales para valores de índice de
                      area.
                    </FormDescription>
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={lightingForm.control}
                name="luxRounding"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Redondeo de Luxes</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        value={field.value}
                        onChange={(e) =>
                          field.onChange(Number(e.target.value))
                        }
                        step="1"
                        className="w-24"
                      />
                    </FormControl>
                    <FormDescription>
                      Número de decimales para medidas en luxes.
                    </FormDescription>
                  </FormItem>
                )}
              />
              <FormField
                control={lightingForm.control}
                name="reflectionRounding"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Redondeo de Reflexión</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        value={field.value}
                        onChange={(e) =>
                          field.onChange(Number(e.target.value))
                        }
                        step="1"
                        className="w-24"
                      />
                    </FormControl>
                    <FormDescription>
                      Número de decimales para valores de porcentaje de
                      reflexión.
                    </FormDescription>
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                onClick={lightingForm.handleSubmit(async (data) => {
                  await lightingConfiguration.mutateAsync(data);
                })}
                disabled={configurationLoading || lightingConfiguration.isPending}
                type="button"
              >
                {lightingConfiguration.isPending ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  "Guardar"
                )}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
} 